// ============================================================================
// MON COMPTE — ce qu'un humain règle pour LUI-MÊME, et pour personne d'autre : sa langue, son mot de passe, ses
// sessions ouvertes.
//
// ⚠️ OUVERT DEPUIS LE PIED DU RAIL (clic sur son nom), pas depuis « Réglages » : ce qu'on règle ici ne vaut que pour soi,
//    quand Réglages règle le MAGASIN (maquette du back-office).
// ⚠️ UN COMPTE SANS MOT DE PASSE LOCAL — le staff Orgazone, qui entre par la connexion Orgazone — VOIT LES CARTES « Mot de
//    passe » et « Double authentification », GRISÉES et marquées « Géré dans la Console » (décision utilisateur du
//    2026-10-03) : l'écran a la même forme pour tous, et dit où se règle ce qu'il ne règle pas. Aucun geste n'y est offert —
//    un formulaire de changement serait un geste qui échoue toujours (§23.0). C'est le serveur qui dit si le compte a un
//    mot de passe local (`/auth/security`), jamais le rôle deviné ici.
// ⚠️ PAS DE DOUBLE AUTHENTIFICATION LOCALE (décision utilisateur du 2026-10-03) : la page de connexion locale, qui sert
//    de secours, ne sait pas demander un second facteur ; l'activer ici enfermerait le compte dehors. La carte DIT où il
//    se règle, elle n'offre pas d'interrupteur.
// ⚠️ LA LANGUE DÉCIDE DES MOTS DES CHAMPS ET DES TEXTES DE L'INTERFACE ; l'écran de CONNEXION suit le navigateur (aucun
//    compte n'y est encore connu) — l'écran le dit, sinon un utilisateur conclurait à un défaut. Après le choix, la page se
//    RECHARGE franchement : les mots sont lus au rendu par des écrans déjà montés, et sans rechargement la moitié de
//    l'application garderait l'ancienne langue.
// ⚠️ LA LISTE DES LANGUES VIENT DU CONTRAT (`VOCABULARY_LANGS`), jamais d'une recopie.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@orgazone/api-envelope';
import { confirm, Icon, rovingRadio, showToast, Trans } from '@orgazone/ui-web';
import { date as formatDate, dateTime } from '@orgazone/date-format';
import { VOCABULARY_LANGS, type VocabularyLang } from '@orgazone/sync-contract';
import { api, type AccountSecurity, type AccountSession } from '../api';
import { useAuth } from '../auth';
import { useStoreScope } from '../store-context';
import { currentVocabularyLang, fieldLabel, platformVocabularyLang } from '../vocabulary';
import { t } from '../i18n';
import { describeAgent, passwordEntry, type PasswordEntry } from './mon-compte-model';

/**
 * Le nom d'une langue, DANS CETTE LANGUE — « Español », jamais « Espagnol » — et ce qu'elle change, dans cette langue
 * aussi. ⚠️ Traduits dans la langue de l'écran, ils seraient illisibles pour qui vient justement changer de langue : ce
 *    sont les seuls textes de cet écran qui ne passent pas par le catalogue (décision écrite dans `scripts/i18n-zones.mjs`).
 */
const LANGUAGES: Readonly<Record<VocabularyLang, { name: string; what: string }>> = {
  fr: { name: 'Français', what: 'Vos champs et vos écrans en français' },
  en: { name: 'English', what: 'Your fields and screens in English' },
  es: { name: 'Español', what: 'Sus campos y pantallas en español' },
};

/** La langue de la plateforme, nommée comme telle : c'est un choix, pas une absence de choix. */
const PLATFORM_CHOICE = '';

/** Ce qu'une lecture du serveur a rendu : en cours, lu, ou son échec avec le motif réel. */
type Loaded<T> = { status: 'loading' } | { status: 'ready'; value: T } | { status: 'error'; message: string };

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

// ── Les mesures de la maquette, une fois ─────────────────────────────────────────────────────────────────────────────
const card: React.CSSProperties = { background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' };
const cardTitle: React.CSSProperties = { display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' };
const cardSub: React.CSSProperties = { display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3, lineHeight: 1.5 };
const field: React.CSSProperties = {
  width: '100%', height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', fontSize: 'var(--t-lead)', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box',
};
const fieldLabelStyle: React.CSSProperties = { display: 'block', fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 };
const secondaryButton: React.CSSProperties = {
  height: 34, padding: '0 13px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
  color: 'var(--ink-800)', fontSize: 'var(--t-body)', fontWeight: 600, cursor: 'pointer',
};

/** L'EN-TÊTE — qui est connecté, et ce que cet écran engage : soi seul. */
export function AccountHeader({ name, email, role, storeCount }: { name: string; email: string; role: string; storeCount: number | null }) {
  const details = [email, role, storeCount === null ? null : t('{n} magasin', { n: storeCount })].filter((x): x is string => !!x);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
      <span style={{ width: 52, height: 52, borderRadius: 'var(--r-pill)', background: 'var(--teal-700)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, flexShrink: 0 }}>
        {(name || '—').slice(0, 2).toUpperCase()}
      </span>
      <div style={{ flex: 1, minWidth: 250 }}>
        <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{name}</h1>
        <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>{details.join(' · ')}</p>
      </div>
      <span style={{ height: 26, display: 'inline-flex', alignItems: 'center', padding: '0 10px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-note)', fontWeight: 700 }}>
        {t('Ce qui se règle ici ne vaut que pour vous')}
      </span>
    </div>
  );
}

/** LA CARTE « MA LANGUE » — une liste de choix, chaque langue nommée dans la sienne. */
export function LanguageCard({ chosen, busy, onPick }: { chosen: string; busy: boolean; onPick: (lang: string) => void }) {
  const active = currentVocabularyLang();
  // ⚠️ LE MOT RÉELLEMENT AFFICHÉ, surcharge du magasin comprise : c'est ce que l'utilisateur verra sur ses écrans.
  const example = fieldLabel('season');
  const choices: { key: string; name: string; what: string }[] = [
    { key: PLATFORM_CHOICE, name: t('Celle de la plateforme — {v}', { v: LANGUAGES[platformVocabularyLang()].name }), what: t('Vous suivez la langue que la plateforme emploie par défaut') },
    ...VOCABULARY_LANGS.map((l) => ({ key: l, name: LANGUAGES[l].name, what: LANGUAGES[l].what })),
  ];
  return (
    <div style={card}>
      <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--ink-200)' }}>
        <span style={cardTitle}>{t('Ma langue')}</span>
        <span style={cardSub}>
          <Trans k='Elle décide des <1>mots de vos champs d’article</1> — en-têtes, filtres, fichiers exportés, procès-verbal d’inventaire, messages du serveur — et des titres et boutons du back-office.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        </span>
      </div>
      <div role="radiogroup" aria-label={t('Ma langue')} style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        {choices.map((c, i) => {
          const on = c.key === chosen;
          return (
            <button key={c.key || 'platform'} type="button" role="radio" aria-checked={on} disabled={busy}
              {...rovingRadio({ index: i, count: choices.length, checked: on, anyChecked: choices.some((x) => x.key === chosen), onPick: (k) => onPick(choices[k]!.key) })}
              onClick={() => { if (!on) onPick(c.key); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', textAlign: 'left', cursor: busy ? 'progress' : 'pointer',
                border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)', background: on ? 'var(--teal-100)' : 'var(--paper)',
              }}>
              <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box', background: 'var(--paper)' }}>
                {on && <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{c.name}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{c.what}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ margin: '0 14px 12px', padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
        <Trans k='En vigueur : <1>{v}</1> — vos écrans emploient les mots de cette langue ; celui de la collection d’un article s’y lit « <2>{example}</2> », surcharge de votre magasin comprise (<3>Référentiels → Vocabulaire</3>).' vars={{ v: LANGUAGES[active].name, example }} parts={[(x) => <strong style={{ color: 'var(--ink-800)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-800)' }}>{x}</strong>, (x) => <span style={{ whiteSpace: 'nowrap' }}>{x}</span>]} />
      </div>
      <div style={{ padding: '0 14px 14px', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        <Icon name="info" size={14} stroke={2} />
        <span>
          <Trans k='Changer de langue <1>recharge la page</1> — franchement, plutôt que de repeindre à moitié. L’écran de connexion, lui, suit la langue de votre navigateur : votre compte n’y est pas encore connu.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        </span>
      </div>
    </div>
  );
}

/** Ce que l'écran dit de la saisie, et sa couleur : la règle, ce qui manque, ou « prêt ». */
function entryHint(entry: PasswordEntry, minLength: number): string {
  switch (entry.kind) {
    case 'empty': return t('{n} caractères au moins — une longue phrase facile à retenir vaut mieux qu’un mot compliqué.', { n: minLength });
    case 'short': return t('Encore {n} caractère pour atteindre {min}.', { n: entry.missing, min: String(minLength) });
    case 'mismatch': return t('Les deux saisies ne correspondent pas encore.');
    case 'needCurrent': return t('Saisissez votre mot de passe actuel pour confirmer.');
    case 'ready': return t('Prêt — vos autres sessions seront fermées.');
  }
}

/**
 * LE REPÈRE « GÉRÉ DANS LA CONSOLE » — la pastille « Verrouillé » de Paramètres : ce qu'on voit sans pouvoir y toucher, et
 * pourquoi.
 */
function ConsoleManagedPill() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-sub)', fontWeight: 600, whiteSpace: 'nowrap' }}>
      <Icon name="lock" size={10} stroke={2.4} />
      {t('Géré dans la Console')}
    </span>
  );
}

/** La carte grisée d'un réglage qui se fait ailleurs : son titre, ce qui le règle, et aucun geste. */
function ConsoleManagedCard({ title, note }: { title: string; note: string }) {
  return (
    <div style={{ ...card, background: 'var(--ink-50)' }} aria-disabled="true">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '15px 18px', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 180 }}>
          <span style={{ ...cardTitle, color: 'var(--ink-500)' }}>{title}</span>
          <span style={{ ...cardSub, color: 'var(--ink-400)' }}>{note}</span>
        </span>
        <ConsoleManagedPill />
      </div>
    </div>
  );
}

/**
 * LA CARTE « MOT DE PASSE » — un compte à mot de passe LOCAL seulement.
 * ⚠️ Le bouton ne s'allume que sur une saisie complète (`passwordEntry`) ; le serveur juge quand même, et ses deux refus
 *    sont nommés : mot de passe actuel faux, ou mot de passe trop courant.
 */
export function PasswordCard({ changedAt, minLength, onChanged }: { changedAt: string | null; minLength: number; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const entry = passwordEntry(values, minLength);
  const ready = entry.kind === 'ready' && !busy;
  const set = (k: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await api.changePassword(values.current, values.next);
      setOpen(false);
      setValues({ current: '', next: '', confirm: '' });
      showToast(t('Mot de passe changé — vos autres sessions ont été fermées'), 'ok');
      onChanged();
    } catch (e) {
      const key = e instanceof ApiError ? e.key : null;
      showToast(
        key === 'invalid_credentials' ? t('Le mot de passe actuel ne correspond pas.')
          : key === 'password_policy' ? t('Ce mot de passe est refusé : il est trop court ou trop courant.')
            : t('Mot de passe non changé : {v}', { v: errorText(e) }),
        'err', 10000,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 180 }}>
          <span style={cardTitle}>{t('Mot de passe')}</span>
          {/* ⚠️ AUCUNE DATE INVENTÉE : un mot de passe choisi avant que le serveur ne les tienne n'en a pas. */}
          {changedAt && <span style={cardSub}>{t('Modifié le {v}', { v: formatDate(changedAt) })}</span>}
        </span>
        <button type="button" style={secondaryButton} onClick={() => { setOpen(!open); setValues({ current: '', next: '', confirm: '' }); }}>
          {open ? t('Fermer') : t('Changer')}
        </button>
      </div>
      {open && (
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }}
          style={{ padding: '0 18px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <label style={{ gridColumn: '1 / -1' }}>
            <span style={fieldLabelStyle}>{t('Mot de passe actuel')}</span>
            <input type="password" autoComplete="current-password" value={values.current} onChange={set('current')} style={{ ...field, maxWidth: 360 }} />
          </label>
          <label>
            <span style={fieldLabelStyle}>{t('Nouveau mot de passe')}</span>
            <input type="password" autoComplete="new-password" value={values.next} onChange={set('next')} style={field} />
          </label>
          <label>
            <span style={fieldLabelStyle}>{t('Confirmer')}</span>
            <input type="password" autoComplete="new-password" value={values.confirm} onChange={set('confirm')} style={field} />
          </label>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-note)', color: entry.kind === 'ready' ? 'var(--teal-800)' : 'var(--ink-500)', lineHeight: 1.45 }}>
              {entryHint(entry, minLength)}
            </span>
            <button type="submit" disabled={!ready}
              style={{
                height: 36, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-lead)', fontWeight: 700,
                background: ready ? 'var(--ink-900)' : 'var(--ink-200)', color: ready ? 'var(--paper)' : 'var(--ink-400)', cursor: ready ? 'pointer' : 'not-allowed',
              }}>
              {busy ? t('Enregistrement…') : t('Changer le mot de passe')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/** LA CARTE « DOUBLE AUTHENTIFICATION » — elle dit où le second facteur se règle ; elle n'offre pas d'interrupteur. */
export function TwoFactorCard() {
  return (
    <div style={card}>
      <div style={{ padding: '15px 18px' }}>
        <span style={cardTitle}>{t('Double authentification')}</span>
        <span style={cardSub}>{t('Elle est demandée par la connexion Orgazone (SSO). La connexion locale du back-office, qui sert de secours, n’en demande pas.')}</span>
      </div>
    </div>
  );
}

/** Le nom lisible d'un appareil : son navigateur et son système quand l'en-tête les dit, sinon rien d'inventé. */
function sessionName(s: AccountSession): string {
  const a = describeAgent(s.userAgent);
  if (!a.browser) return t('Navigateur inconnu');
  return a.system ? `${a.browser} · ${a.system}` : a.browser;
}

/** LA CARTE « MES SESSIONS » — chaque appareil connecté, et la fermeture de ceux qu'on ne reconnaît pas. */
export function SessionsCard({ state, closing, onClose, onRetry }: {
  state: Loaded<AccountSession[]>;
  closing: string | null;
  onClose: (s: AccountSession) => void;
  onRetry: () => void;
}) {
  return (
    <div style={card}>
      <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--ink-200)' }}>
        <span style={cardTitle}>{t('Mes sessions')}</span>
        <span style={cardSub}>{t('Les appareils connectés à votre compte — fermez ceux que vous ne reconnaissez pas.')}</span>
      </div>
      {state.status === 'loading' && <div style={{ padding: '14px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture des sessions…')}</div>}
      {state.status === 'error' && (
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 'var(--t-body)', color: 'var(--rose-fg)' }}>
          <span style={{ flex: 1, minWidth: 200 }}>{t('Les sessions ne se lisent pas : {v}', { v: state.message })}</span>
          <button type="button" style={secondaryButton} onClick={onRetry}>{t('Réessayer')}</button>
        </div>
      )}
      {state.status === 'ready' && state.value.length === 0 && (
        <div style={{ padding: '14px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Aucune session ouverte.')}</div>
      )}
      {state.status === 'ready' && state.value.map((s) => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 18px', borderBottom: '1px solid var(--ink-100)' }}>
          <span style={{ width: 32, height: 32, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={describeAgent(s.userAgent).device === 'mobile' ? 'tablet' : 'monitor'} size={15} stroke={1.9} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{sessionName(s)}</span>
              {s.current && (
                <span style={{ height: 18, display: 'inline-flex', alignItems: 'center', padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.03em', textTransform: 'uppercase' }}>
                  {t('Cet appareil')}
                </span>
              )}
            </span>
            <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 1 }}>
              {[s.ip, t('dernière activité le {v}', { v: dateTime(s.lastSeenAt) })].filter((x): x is string => !!x).join(' · ')}
            </span>
          </span>
          {/* ⚠️ LA SESSION DE CE NAVIGATEUR NE SE FERME PAS D'ICI : c'est la déconnexion, au pied du rail. */}
          {!s.current && (
            <button type="button" disabled={closing === s.id} onClick={() => onClose(s)}
              style={{ height: 30, padding: '0 11px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              {t('Fermer')}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * LES CARTES DE SÉCURITÉ — mot de passe et double authentification : réglables pour un compte LOCAL, GRISÉES et dites
 * « gérées dans la Console » pour un compte qui n'a pas de mot de passe ici.
 * ⚠️ Pendant la lecture et en cas d'échec, l'écran le DIT : il ne devine pas si le compte a un mot de passe.
 */
export function SecurityCards({ state, onChanged, onRetry }: { state: Loaded<AccountSecurity>; onChanged: () => void; onRetry: () => void }) {
  if (state.status === 'loading') return <div style={{ ...card, padding: '14px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture de la sécurité du compte…')}</div>;
  if (state.status === 'error') {
    return (
      <div style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 'var(--t-body)', color: 'var(--rose-fg)' }}>
        <span style={{ flex: 1, minWidth: 200 }}>{t('La sécurité du compte ne se lit pas : {v}', { v: state.message })}</span>
        <button type="button" style={secondaryButton} onClick={onRetry}>{t('Réessayer')}</button>
      </div>
    );
  }
  if (!state.value.localPassword) {
    return (
      <>
        <ConsoleManagedCard title={t('Mot de passe')}
          note={t('Votre compte se connecte par la connexion Orgazone : son mot de passe se change dans la Console, pas ici.')} />
        <ConsoleManagedCard title={t('Double authentification')}
          note={t('Elle se règle dans la Console, avec votre compte Orgazone.')} />
      </>
    );
  }
  return (
    <>
      <PasswordCard changedAt={state.value.passwordChangedAt} minLength={state.value.passwordMinLength} onChanged={onChanged} />
      <TwoFactorCard />
    </>
  );
}

export function MonCompte() {
  const { user, refresh } = useAuth();
  const { view } = useStoreScope();
  const [busy, setBusy] = useState(false);
  // La valeur MONTRÉE est celle du compte, pas celle qui est en vigueur : un compte sans langue doit lire « Celle de la
  // plateforme », même si l'application, elle, rend en français.
  const [chosen, setChosen] = useState<string>(user?.locale ?? PLATFORM_CHOICE);
  const [security, setSecurity] = useState<Loaded<AccountSecurity>>({ status: 'loading' });
  const [sessions, setSessions] = useState<Loaded<AccountSession[]>>({ status: 'loading' });
  const [closing, setClosing] = useState<string | null>(null);

  const loadSecurity = useCallback(() => {
    setSecurity({ status: 'loading' });
    api.accountSecurity().then((value) => setSecurity({ status: 'ready', value })).catch((e) => setSecurity({ status: 'error', message: errorText(e) }));
  }, []);
  const loadSessions = useCallback(() => {
    setSessions({ status: 'loading' });
    api.mySessions().then((r) => setSessions({ status: 'ready', value: r.sessions })).catch((e) => setSessions({ status: 'error', message: errorText(e) }));
  }, []);
  useEffect(() => { loadSecurity(); loadSessions(); }, [loadSecurity, loadSessions]);

  const pickLanguage = async (next: string) => {
    if (busy) return;
    const before = chosen;
    setChosen(next);
    setBusy(true);
    try {
      await api.setMyLang(next === PLATFORM_CHOICE ? null : next);
      await refresh();
      // ⚠️ Rechargement FRANC : voir l'en-tête. Le toast ne serait pas lu, la page part aussitôt.
      window.location.reload();
    } catch (e) {
      setChosen(before); // L'écran revient à ce qui est VRAI en base, jamais à ce qu'on a tenté.
      setBusy(false);
      showToast(t('Langue non changée : {v}', { v: errorText(e) }), 'err', 10000);
    }
  };

  const closeSession = async (s: AccountSession) => {
    const ok = await confirm({
      title: t('Fermer la session de « {v} » ?', { v: sessionName(s) }),
      message: t('L’appareil devra se reconnecter.'),
      confirmLabel: t('Fermer la session'),
      tone: 'danger',
    });
    if (!ok) return;
    setClosing(s.id);
    try {
      await api.closeSession(s.id);
      showToast(t('Session fermée'), 'ok');
      loadSessions();
    } catch (e) {
      showToast(t('Session non fermée : {v}', { v: errorText(e) }), 'err', 10000);
    } finally {
      setClosing(null);
    }
  };

  if (!user) return <p style={{ color: 'var(--ink-500)' }}>{t('Session non résolue')}</p>;
  return (
    <div>
      <AccountHeader name={user.name} email={user.email} role={user.roleLabel ? t(user.roleLabel) : ''} storeCount={view ? view.stores.length : null} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, alignItems: 'start' }}>
        <LanguageCard chosen={chosen} busy={busy} onPick={(l) => { void pickLanguage(l); }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* ⚠️ Un changement de mot de passe ferme les AUTRES sessions : la liste se relit avec la date. */}
          <SecurityCards state={security} onChanged={() => { loadSecurity(); loadSessions(); }} onRetry={loadSecurity} />
          <SessionsCard state={sessions} closing={closing} onClose={(s) => { void closeSession(s); }} onRetry={loadSessions} />
        </div>
      </div>
    </div>
  );
}
